import type { NotifyLevel } from "./notifier";

export type FormattedError = Readonly<{
  level: NotifyLevel;
  message: string;
  description?: string;
  /**
   * Optional stable key for dedupe/persistence.
   */
  key?: string;
}>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Best-effort formatting for errors coming from:
 * - Apollo (`ApolloError`, GraphQL errors, network errors)
 * - Atom framework JSON-encoded permission errors
 * - plain thrown Errors
 */
export function formatUnknownError(
  err: unknown,
  options?: { defaultMessage?: string; keyPrefix?: string },
): FormattedError {
  const defaultMessage = options?.defaultMessage ?? "Anfrage fehlgeschlagen";
  const keyPrefix = options?.keyPrefix ?? "err";

  if (err instanceof Error) {
    // Try to parse JSON-encoded backend errors (permission errors etc.)
    try {
      const parsed = JSON.parse(err.message) as unknown;
      if (isRecord(parsed) && typeof parsed.message === "string") {
        const errorCode =
          isRecord(parsed.debugInfos) &&
          typeof parsed.debugInfos.errorCode === "string"
            ? parsed.debugInfos.errorCode
            : undefined;
        return {
          level: "error",
          message: parsed.message,
          description: errorCode ? `Code: ${errorCode}` : undefined,
          key: errorCode ? `${keyPrefix}:${errorCode}` : undefined,
        };
      }
    } catch {
      // ignore
    }

    return {
      level: "error",
      message: err.message || defaultMessage,
      key: err.message ? `${keyPrefix}:${err.message}` : undefined,
    };
  }

  if (typeof err === "string") {
    return {
      level: "error",
      message: err || defaultMessage,
      key: `${keyPrefix}:${err}`,
    };
  }

  if (isRecord(err)) {
    // GraphQL errors often look like { message, extensions, ... }
    const msg = typeof err.message === "string" ? err.message : undefined;
    const code =
      isRecord(err.extensions) && typeof err.extensions.code === "string"
        ? err.extensions.code
        : undefined;
    return {
      level: "error",
      message: msg ?? defaultMessage,
      description: code ? `Code: ${code}` : undefined,
      key: code
        ? `${keyPrefix}:${code}`
        : msg
          ? `${keyPrefix}:${msg}`
          : undefined,
    };
  }

  return { level: "error", message: defaultMessage };
}
